import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { CreateWorkOrderModal } from '../../components/work-orders/CreateWorkOrderModal';
import { 
  ClipboardList, Plus, RefreshCw, Search, Calendar, User, Users,
  Clock, AlertCircle, AlertTriangle, X, Loader2,
  MessageSquare, Paperclip, Activity, ExternalLink, MapPin, Building2,
  ChevronLeft, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission constants
const WORK_ORDER_MANAGE_ROLES = ['project_manager', 'operations_manager', 'admin', 'super_admin'];
const WORK_ORDER_ASSIGN_ROLES = ['operations_manager', 'admin', 'super_admin'];

// Status configuration
const STATUS_CONFIG = {
  open: { label: 'Open', bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/50' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/50' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/50' },
  blocked: { label: 'Blocked', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' },
  completed: { label: 'Completed', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/50' },
  closed: { label: 'Closed', bg: 'bg-slate-700/30', text: 'text-slate-500', border: 'border-slate-700' },
  canceled: { label: 'Canceled', bg: 'bg-slate-700/30', text: 'text-slate-500', border: 'border-slate-700 line-through' },
};

// Priority configuration
const PRIORITY_CONFIG = {
  low: { label: 'Low', text: 'text-slate-400', icon: null },
  medium: { label: 'Medium', text: 'text-blue-400', icon: null },
  high: { label: 'High', text: 'text-amber-400', icon: AlertCircle },
  urgent: { label: 'Urgent', text: 'text-red-400 font-bold', icon: AlertTriangle },
};

// Category options
const CATEGORIES = [
  { value: 'repair', label: 'Repair' },
  { value: 'punch', label: 'Punch' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'service', label: 'Service' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'ops_task', label: 'Ops Task' },
];

// Status transition options per current status
const STATUS_TRANSITIONS = {
  open: ['scheduled', 'in_progress', 'canceled'],
  scheduled: ['in_progress', 'open', 'canceled'],
  in_progress: ['completed', 'blocked', 'canceled'],
  blocked: ['in_progress', 'canceled'],
  completed: ['closed', 'in_progress'],
  closed: [],
  canceled: [],
};

// Helper functions
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || ['completed', 'closed', 'canceled'].includes(status)) return false;
  return new Date(dueDate) < new Date();
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <Badge className={`${config.bg} ${config.text} ${config.border} border`}>
      {config.label}
    </Badge>
  );
};

// Priority badge component
const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  const Icon = config.icon;
  return (
    <span className={`flex items-center gap-1 ${config.text}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {config.label}
    </span>
  );
};


export const WorkOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [workOrders, setWorkOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [crews, setCrews] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Filter state - initialized from URL params
  const [filters, setFilters] = useState(() => ({
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    market_id: searchParams.get('market_id') || '',
    assigned_crew_id: searchParams.get('assigned_crew_id') || '',
    assigned_user_id: searchParams.get('assigned_user_id') || '',
    overdue: searchParams.get('overdue') === 'true',
    search: searchParams.get('search') || '',
  }));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Active preset state (for highlighting)
  const [activePreset, setActivePreset] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Detail data
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteIsInternal, setNoteIsInternal] = useState(false);
  
  // Permission checks
  const canManage = user?.role && WORK_ORDER_MANAGE_ROLES.includes(user.role.toLowerCase());
  const canAssign = user?.role && WORK_ORDER_ASSIGN_ROLES.includes(user.role.toLowerCase());
  
  // Detect active preset from current filters
  useEffect(() => {
    if (filters.assigned_user_id === user?.id && !filters.status && !filters.priority && !filters.overdue) {
      setActivePreset('my_assigned');
    } else if (filters.overdue && !filters.status && !filters.priority && !filters.assigned_user_id) {
      setActivePreset('overdue');
    } else if (filters.priority === 'urgent' && !filters.status && !filters.overdue && !filters.assigned_user_id) {
      setActivePreset('urgent');
    } else if (filters.status === 'open,scheduled' && !filters.priority && !filters.overdue && !filters.assigned_user_id) {
      setActivePreset('open_scheduled');
    } else {
      setActivePreset(null);
    }
  }, [filters, user?.id]);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.market_id) params.append('market_id', filters.market_id);
      if (filters.assigned_crew_id) params.append('assigned_crew_id', filters.assigned_crew_id);
      if (filters.assigned_user_id) params.append('assigned_user_id', filters.assigned_user_id);
      if (filters.overdue) params.append('overdue', 'true');
      if (filters.search) params.append('search', filters.search);
      params.append('page', page);
      params.append('limit', 25);
      
      const [woRes, statsRes, marketsRes, crewsRes] = await Promise.all([
        axios.get(`${API}/ops/work-orders?${params.toString()}`),
        axios.get(`${API}/ops/work-orders/stats`),
        axios.get(`${API}/markets`),
        axios.get(`${API}/ops/crews`).catch(() => ({ data: { crews: [] } })),
      ]);
      
      setWorkOrders(woRes.data.work_orders || []);
      setTotalPages(woRes.data.total_pages || 1);
      setStats(statsRes.data);
      setMarkets((marketsRes.data || []).filter(m => m.is_canonical && m.is_active));
      setCrews(crewsRes.data.crews || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch users for assignment
  useEffect(() => {
    const fetchUsers = async () => {
      if (canAssign) {
        try {
          const res = await axios.get(`${API}/users`);
          setUsers(res.data || []);
        } catch (err) {
          console.error('Error fetching users:', err);
        }
      }
    };
    fetchUsers();
  }, [canAssign]);
  
  // Fetch detail data when work order is selected
  useEffect(() => {
    const fetchDetailData = async () => {
      if (!selectedWorkOrder) return;
      
      try {
        const [notesRes, filesRes, activityRes] = await Promise.all([
          axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/notes`),
          axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/files`),
          axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/activity`),
        ]);
        
        setNotes(notesRes.data.notes || []);
        setFiles(filesRes.data.files || []);
        setActivity(activityRes.data.activities || []);
      } catch (err) {
        console.error('Error fetching detail data:', err);
      }
    };
    
    if (detailSheetOpen && selectedWorkOrder) {
      fetchDetailData();
    }
  }, [selectedWorkOrder, detailSheetOpen]);
  
  // Handle filter change with URL sync
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPage(1);
    
    // Update URL params (explicit action, not effect-based)
    const newParams = new URLSearchParams();
    if (newFilters.status) newParams.set('status', newFilters.status);
    if (newFilters.priority) newParams.set('priority', newFilters.priority);
    if (newFilters.market_id) newParams.set('market_id', newFilters.market_id);
    if (newFilters.assigned_crew_id) newParams.set('assigned_crew_id', newFilters.assigned_crew_id);
    if (newFilters.assigned_user_id) newParams.set('assigned_user_id', newFilters.assigned_user_id);
    if (newFilters.overdue) newParams.set('overdue', 'true');
    if (newFilters.search) newParams.set('search', newFilters.search);
    setSearchParams(newParams, { replace: true });
  };
  
  // Apply preset filter
  const applyPreset = (preset) => {
    let newFilters = {
      status: '',
      priority: '',
      market_id: filters.market_id, // preserve market filter
      assigned_crew_id: '',
      assigned_user_id: '',
      overdue: false,
      search: '',
    };
    
    switch (preset) {
      case 'my_assigned':
        newFilters.assigned_user_id = user?.id || '';
        break;
      case 'overdue':
        newFilters.overdue = true;
        break;
      case 'urgent':
        newFilters.priority = 'urgent';
        break;
      case 'open_scheduled':
        newFilters.status = 'open,scheduled';
        break;
      default:
        break;
    }
    
    setFilters(newFilters);
    setPage(1);
    
    // Update URL params
    const newParams = new URLSearchParams();
    if (newFilters.status) newParams.set('status', newFilters.status);
    if (newFilters.priority) newParams.set('priority', newFilters.priority);
    if (newFilters.market_id) newParams.set('market_id', newFilters.market_id);
    if (newFilters.assigned_user_id) newParams.set('assigned_user_id', newFilters.assigned_user_id);
    if (newFilters.overdue) newParams.set('overdue', 'true');
    setSearchParams(newParams, { replace: true });
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilters({ 
      status: '', 
      priority: '', 
      market_id: '', 
      assigned_crew_id: '', 
      assigned_user_id: '',
      overdue: false,
      search: '' 
    });
    setPage(1);
    setSearchParams({}, { replace: true });
  };
  
  // Check if any filter is active
  const hasActiveFilters = filters.status || filters.priority || filters.market_id || 
    filters.assigned_crew_id || filters.assigned_user_id || filters.overdue || filters.search;
  
  // Open work order detail
  const openDetail = (wo) => {
    setSelectedWorkOrder(wo);
    setActiveTab('overview');
    setDetailSheetOpen(true);
  };
  
  // Handle create modal success
  const handleCreateSuccess = () => {
    fetchData();
  };
  
  // Update work order status
  const handleStatusChange = async (newStatus) => {
    if (!selectedWorkOrder) return;
    
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/ops/work-orders/${selectedWorkOrder.id}`, { status: newStatus });
      setSelectedWorkOrder(res.data);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchData();
      
      // Refresh activity
      const activityRes = await axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/activity`);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };
  
  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedWorkOrder) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/ops/work-orders/${selectedWorkOrder.id}/notes`, {
        body: newNote.trim(),
        is_internal: noteIsInternal,
      });
      toast.success('Note added');
      setNewNote('');
      setNoteIsInternal(false);
      
      // Refresh notes and activity
      const [notesRes, activityRes] = await Promise.all([
        axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/notes`),
        axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/activity`),
      ]);
      setNotes(notesRes.data.notes || []);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading && workOrders.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-slate-400">Loading work orders...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6 bg-slate-900 min-h-screen" data-testid="work-orders-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <ClipboardList className="h-8 w-8 text-blue-500" />
              Work Orders
            </h1>
            <p className="text-slate-400 mt-1">Manage service, repair, and follow-up work orders</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData} size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {canManage && (
              <Button onClick={() => setCreateModalOpen(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="create-wo-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Work Order
              </Button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-all">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Open</CardTitle>
                <ClipboardList className="h-4 w-4 text-slate-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white" data-testid="stat-open">{stats.open_count}</div>
                <p className="text-xs text-slate-500 mt-1">work orders</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-all">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">In Progress</CardTitle>
                <Clock className="h-4 w-4 text-amber-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white" data-testid="stat-in-progress">{stats.in_progress_count}</div>
                <p className="text-xs text-slate-500 mt-1">work orders</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700 hover:border-red-900/50 transition-all">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Overdue</CardTitle>
                <AlertCircle className="h-4 w-4 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-400" data-testid="stat-overdue">{stats.overdue_count}</div>
                <p className="text-xs text-slate-500 mt-1">past due</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700 hover:border-red-900/50 transition-all">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Urgent</CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-400" data-testid="stat-urgent">{stats.urgent_count}</div>
                <p className="text-xs text-slate-500 mt-1">priority</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700">
          <Select value={filters.market_id} onValueChange={(v) => handleFilterChange('market_id', v)}>
            <SelectTrigger className="w-[160px] bg-slate-700 border-slate-600 text-slate-300">
              <SelectValue placeholder="All Markets" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Markets</SelectItem>
              {markets.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
            <SelectTrigger className="w-[140px] bg-slate-700 border-slate-600 text-slate-300">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filters.priority} onValueChange={(v) => handleFilterChange('priority', v)}>
            <SelectTrigger className="w-[130px] bg-slate-700 border-slate-600 text-slate-300">
              <SelectValue placeholder="All Priority" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Priority</SelectItem>
              {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filters.assigned_crew_id} onValueChange={(v) => handleFilterChange('assigned_crew_id', v)}>
            <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-slate-300">
              <SelectValue placeholder="All Crews" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Crews</SelectItem>
              {crews.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search WO#, title, job..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 bg-slate-700 border-slate-600 text-slate-300 placeholder:text-slate-500"
                data-testid="search-input"
              />
            </div>
          </div>
          
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-slate-400 hover:text-white">
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Preset Filter Chips */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="preset-filters">
          <span className="text-sm text-slate-500 mr-1">Quick Filters:</span>
          
          <Button
            variant={activePreset === 'my_assigned' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => activePreset === 'my_assigned' ? clearFilters() : applyPreset('my_assigned')}
            className={activePreset === 'my_assigned' 
              ? 'bg-blue-600 text-white hover:bg-blue-500' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700'}
            data-testid="preset-my-assigned"
          >
            <User className="h-3.5 w-3.5 mr-1.5" />
            My Assigned
            {activePreset === 'my_assigned' && <X className="h-3 w-3 ml-1.5" />}
          </Button>
          
          <Button
            variant={activePreset === 'overdue' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => activePreset === 'overdue' ? clearFilters() : applyPreset('overdue')}
            className={activePreset === 'overdue' 
              ? 'bg-red-600 text-white hover:bg-red-500' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700'}
            data-testid="preset-overdue"
          >
            <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
            Overdue
            {activePreset === 'overdue' && <X className="h-3 w-3 ml-1.5" />}
          </Button>
          
          <Button
            variant={activePreset === 'urgent' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => activePreset === 'urgent' ? clearFilters() : applyPreset('urgent')}
            className={activePreset === 'urgent' 
              ? 'bg-red-600 text-white hover:bg-red-500' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700'}
            data-testid="preset-urgent"
          >
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            Urgent
            {activePreset === 'urgent' && <X className="h-3 w-3 ml-1.5" />}
          </Button>
          
          <Button
            variant={activePreset === 'open_scheduled' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => activePreset === 'open_scheduled' ? clearFilters() : applyPreset('open_scheduled')}
            className={activePreset === 'open_scheduled' 
              ? 'bg-blue-600 text-white hover:bg-blue-500' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700'}
            data-testid="preset-open-scheduled"
          >
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
            Open + Scheduled
            {activePreset === 'open_scheduled' && <X className="h-3 w-3 ml-1.5" />}
          </Button>
        </div>

        {/* Data Table */}
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg overflow-hidden">
          {workOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ClipboardList className="h-16 w-16 text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No work orders found</h3>
              <p className="text-slate-400 mb-4">
                {hasActiveFilters 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create your first work order to start tracking ops work.'}
              </p>
              {canManage && !hasActiveFilters && (
                <Button onClick={() => setCreateModalOpen(true)} className="bg-blue-600 hover:bg-blue-500">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Work Order
                </Button>
              )}
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-slate-800/80 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">WO #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Market</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Assigned</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map(wo => (
                    <tr
                      key={wo.id}
                      onClick={() => openDetail(wo)}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors cursor-pointer"
                      data-testid={`wo-row-${wo.work_order_number}`}
                    >
                      <td className="px-4 py-3 text-sm font-mono text-blue-400">{wo.work_order_number}</td>
                      <td className="px-4 py-3 text-sm text-slate-300 max-w-[200px] truncate">{wo.title}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        <div className="truncate max-w-[180px]">{wo.job_name || '—'}</div>
                        <div className="text-xs text-slate-500 truncate">{wo.customer_name}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">{wo.market_name || '—'}</td>
                      <td className="px-4 py-3 text-sm"><PriorityBadge priority={wo.priority} /></td>
                      <td className="px-4 py-3 text-sm"><StatusBadge status={wo.status} /></td>
                      <td className="px-4 py-3 text-sm text-slate-300">{wo.assigned_crew_name || wo.assigned_user_name || '—'}</td>
                      <td className={`px-4 py-3 text-sm ${isOverdue(wo.due_date, wo.status) ? 'text-red-400' : 'text-slate-400'}`}>
                        {formatDate(wo.due_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
                  <span className="text-sm text-slate-400">Page {page} of {totalPages}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="border-slate-600"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="border-slate-600"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Create Modal - Using extracted component */}
        <CreateWorkOrderModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />

        {/* Detail Sheet */}
        <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
          <SheetContent className="w-[600px] sm:w-[540px] bg-slate-800 border-l border-slate-700 text-slate-300 overflow-y-auto">
            {selectedWorkOrder && (
              <>
                <SheetHeader className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <SheetTitle className="text-xl text-white">{selectedWorkOrder.title}</SheetTitle>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
                        <Building2 className="h-3 w-3" />
                        <span>{selectedWorkOrder.job_name || 'Project'}</span>
                        <span>•</span>
                        <MapPin className="h-3 w-3" />
                        <span>{selectedWorkOrder.market_name}</span>
                      </div>
                    </div>
                    <Badge className="font-mono bg-slate-700 text-slate-300">{selectedWorkOrder.work_order_number}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority={selectedWorkOrder.priority} />
                    <StatusBadge status={selectedWorkOrder.status} />
                  </div>
                </SheetHeader>

                <Separator className="my-4 bg-slate-700" />

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="bg-slate-900/50 p-1 rounded-lg border border-slate-700/50 w-full grid grid-cols-4">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                      Notes ({notes.length})
                    </TabsTrigger>
                    <TabsTrigger value="files" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                      Files ({files.length})
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                      Activity
                    </TabsTrigger>
                  </TabsList>

                  {/* Overview Tab */}
                  <TabsContent value="overview" className="mt-4 space-y-4">
                    {/* Status Control */}
                    {canManage && STATUS_TRANSITIONS[selectedWorkOrder.status]?.length > 0 && (
                      <div className="p-4 bg-slate-700/30 rounded-lg border border-slate-700">
                        <Label className="text-sm text-slate-400 mb-2 block">Change Status</Label>
                        <div className="flex flex-wrap gap-2">
                          {STATUS_TRANSITIONS[selectedWorkOrder.status].map(newStatus => (
                            <Button
                              key={newStatus}
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(newStatus)}
                              disabled={saving}
                              className="border-slate-600"
                            >
                              {STATUS_CONFIG[newStatus]?.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Assignment */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <Label className="text-xs text-slate-500 uppercase">Assigned Crew</Label>
                        <div className="mt-1 flex items-center gap-2 text-white">
                          <Users className="h-4 w-4 text-slate-400" />
                          {selectedWorkOrder.assigned_crew_name || '—'}
                        </div>
                      </div>
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <Label className="text-xs text-slate-500 uppercase">Assigned User</Label>
                        <div className="mt-1 flex items-center gap-2 text-white">
                          <User className="h-4 w-4 text-slate-400" />
                          {selectedWorkOrder.assigned_user_name || '—'}
                        </div>
                      </div>
                    </div>

                    {/* Schedule */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <Label className="text-xs text-slate-500 uppercase">Scheduled Start</Label>
                        <div className="mt-1 flex items-center gap-2 text-white">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDateTime(selectedWorkOrder.scheduled_start)}
                        </div>
                      </div>
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <Label className="text-xs text-slate-500 uppercase">Due Date</Label>
                        <div className={`mt-1 flex items-center gap-2 ${isOverdue(selectedWorkOrder.due_date, selectedWorkOrder.status) ? 'text-red-400' : 'text-white'}`}>
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDate(selectedWorkOrder.due_date)}
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="p-3 bg-slate-700/30 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Category</span>
                        <span className="text-white capitalize">{selectedWorkOrder.category?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Created</span>
                        <span className="text-white">{formatDateTime(selectedWorkOrder.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Requested by</span>
                        <span className="text-white">{selectedWorkOrder.requested_by_name || '—'}</span>
                      </div>
                    </div>

                    {/* Description */}
                    {selectedWorkOrder.description && (
                      <div className="p-3 bg-slate-700/30 rounded-lg">
                        <Label className="text-xs text-slate-500 uppercase mb-2 block">Description</Label>
                        <p className="text-slate-300 whitespace-pre-wrap">{selectedWorkOrder.description}</p>
                      </div>
                    )}

                    {/* Linked Project */}
                    <div className="p-3 bg-slate-700/30 rounded-lg">
                      <Label className="text-xs text-slate-500 uppercase mb-2 block">Linked Project</Label>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-white font-medium">{selectedWorkOrder.job_name || 'Project'}</div>
                          <div className="text-sm text-slate-400">{selectedWorkOrder.customer_name}</div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-blue-400 hover:text-blue-300"
                          disabled={!selectedWorkOrder.job_id}
                          onClick={() => {
                            if (selectedWorkOrder.job_id) {
                              navigate(`/jobs/${selectedWorkOrder.job_id}`);
                            }
                          }}
                          title={!selectedWorkOrder.job_id ? 'No linked project' : 'View project details'}
                          data-testid="view-project-btn"
                        >
                          View Project <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Notes Tab */}
                  <TabsContent value="notes" className="mt-4 space-y-4">
                    {/* Add Note Form */}
                    {canManage && (
                      <div className="p-4 bg-slate-700/30 rounded-lg border border-slate-700 space-y-3">
                        <Textarea
                          placeholder="Add a note..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="bg-slate-700 border-slate-600 min-h-[80px]"
                        />
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Switch checked={noteIsInternal} onCheckedChange={setNoteIsInternal} />
                            <Label className="text-sm text-slate-400">Internal (ops only)</Label>
                          </div>
                          <Button onClick={handleAddNote} disabled={!newNote.trim() || saving} size="sm" className="bg-blue-600 hover:bg-blue-500">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Note'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Notes List */}
                    {notes.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No notes yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {notes.map(note => (
                          <div key={note.id} className="p-3 bg-slate-700/30 rounded-lg border border-slate-700">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="h-4 w-4 text-slate-400" />
                              <span className="text-sm font-medium text-white">{note.author_name || 'User'}</span>
                              <span className="text-xs text-slate-500">{formatRelativeTime(note.created_at)}</span>
                              {note.is_internal && (
                                <Badge className="text-xs bg-slate-600 text-slate-300">Internal</Badge>
                              )}
                            </div>
                            <p className="text-slate-300 whitespace-pre-wrap">{note.body}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Files Tab */}
                  <TabsContent value="files" className="mt-4 space-y-4">
                    {files.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No files linked</p>
                        <p className="text-xs mt-1">Files can be linked from the parent job</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {files.map(file => (
                          <div key={file.id} className="p-3 bg-slate-700/30 rounded-lg border border-slate-700">
                            <div className="flex items-center gap-2 mb-2">
                              <Paperclip className="h-4 w-4 text-slate-400" />
                              <span className="text-sm text-white truncate">{file.file_name}</span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {file.uploaded_by_name} • {formatRelativeTime(file.created_at)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Activity Tab */}
                  <TabsContent value="activity" className="mt-4 space-y-3">
                    {activity.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No activity yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {activity.map(act => (
                          <div key={act.id} className="flex gap-3 p-3 bg-slate-700/30 rounded-lg border border-slate-700">
                            <div className="h-8 w-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                              <Activity className="h-4 w-4 text-slate-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white">
                                {act.event_type === 'created' && 'Work order created'}
                                {act.event_type === 'status_changed' && (
                                  <>Status changed: <span className="text-slate-400">{act.old_value}</span> → <span className="text-blue-400">{act.new_value}</span></>
                                )}
                                {act.event_type === 'assigned' && `Assigned to ${act.new_value}`}
                                {act.event_type === 'unassigned' && `Unassigned from ${act.old_value}`}
                                {act.event_type === 'note_added' && 'Note added'}
                                {act.event_type === 'file_added' && 'File linked'}
                                {act.event_type === 'file_removed' && 'File unlinked'}
                                {act.event_type === 'scheduled' && `${act.field_changed} updated`}
                                {act.event_type === 'closed' && 'Work order closed'}
                                {act.event_type === 'canceled' && 'Work order canceled'}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                {act.actor_name || 'System'} • {formatRelativeTime(act.created_at)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
};

export default WorkOrders;
